/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Anchor, Button, List, ListItem, Modal, StatusBadge, Text, Title } from '@/shared/ui';
import { NetworkRow, Toggle } from '../FacebookSettingsSection/FacebookSettingsParts';
import type { PortalSettings } from './constants';
import { ConfigButton, CopyButton } from './widgets';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Portals the Canal Pro feed is switched on with (the Grupo OLX also shows them on OLX). */
export const CANAL_PRO_PORTALS = ['zap', 'vivareal'] as const;

/**
 * Canal Pro's sign-in page. It is a single-page app, so a deeper link to
 * "Integração de anúncios" cannot be relied on; the step text gives the path.
 */
export const CANAL_PRO_URL = 'https://canalpro.grupozap.com/';

/** Whether the agency has the Canal Pro feed on. */
export function canalProEnabled(settings: PortalSettings): boolean {
  return CANAL_PRO_PORTALS.some((portal) => settings.enabled_portals.includes(portal));
}

/**
 * The agency's VRSync feed address, served under the SPA host's ``/api``
 * (CloudFront routes it to the backend). It carries the agency id itself: the
 * old slug form only round-trips generated ids ("nome-ABCD"), so an id like
 * "crawler-data" came back as "crawler-DATA" and the feed answered 404.
 */
export function canalProFeedUrl(origin: string, agencyId: string): string {
  return `${origin}/api/portal-integration/feeds/vrsync/${encodeURIComponent(agencyId)}.xml`;
}

export interface CanalProBlockProps {
  settings: PortalSettings;
  feedUrl: string;
  saving: boolean;
  onToggle: () => void;
  /** Whether the settings modal is open (the section opens it on connect). */
  configOpen: boolean;
  onConfigOpenChange: (open: boolean) => void;
  t: Translate;
}

/**
 * Canal Pro (Grupo OLX): one XML feed publishes on ZAP, Viva Real and OLX.
 * Leads come back to the endpoint the Grupo OLX homologated for Housien
 * (one for every agency, routed by listing), so there is no per-agency lead
 * link to paste (https://developers.grupozap.com/webhooks/integration.html).
 */
export function CanalProBlock({
  settings, feedUrl, saving, onToggle, configOpen, onConfigOpenChange, t,
}: CanalProBlockProps) {
  const enabled = canalProEnabled(settings);

  if (!enabled) {
    // Off: one row with a connect button, as Redes Sociais shows a network
    // not connected yet.
    return (
      <div data-testid="canal-pro-block">
        <NetworkRow
          testId="network-row-canal-pro"
          icon={<CanalProBadge />}
          title={t('canalPro.titulo')}
          hint={t('canalPro.dicaConectar')}
          trailing={(
            <Button variant="outlined" size="xs" data-testid="canal-pro-connect-btn" disabled={saving} onClick={onToggle}>
              {t('canalPro.conectar')}
            </Button>
          )}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-(--space-4)" data-testid="canal-pro-block">
      <NetworkRow
        testId="network-row-canal-pro"
        icon={<CanalProBadge />}
        title={t('canalPro.titulo')}
        identity={t('canalPro.descricao')}
        trailing={<Toggle checked={enabled} testId="toggle-canal-pro" disabled={saving} onClick={onToggle} />}
        meta={(
          <>
            {/* Housien never hears back from Canal Pro, so these say what is
                set up here, not that the Grupo OLX is reading or posting. */}
            <StatusBadge tone={enabled ? 'info' : 'neutral'}>
              {enabled ? t('canalPro.feedAtivo') : t('canalPro.feedInativo')}
            </StatusBadge>
            <ConfigButton
              onClick={() => onConfigOpenChange(true)}
              label={t('portal.configuracoes')} testId="canal-pro-config-btn"
            />
          </>
        )}
      />

      <Modal
        opened={configOpen}
        onClose={() => onConfigOpenChange(false)}
        title={t('canalPro.configuracoesTitulo')}
        size="2xl"
      >
        <div className="flex flex-col gap-(--space-4) max-h-[70vh] overflow-y-auto" data-testid="canal-pro-settings">
          <Text
            className="m-0 text-xs leading-relaxed p-(--space-3) rounded-(--radius-md) bg-[color-mix(in_srgb,var(--status-info)_10%,var(--surface-primary))] text-(--text-secondary)"
            data-testid="canal-pro-connection-note"
          >
            {t('canalPro.avisoConexao')}
          </Text>
          <Step number={1} title={t('canalPro.passoFeed')} t={t}>
            <UrlField value={feedUrl} testId="canal-pro-feed-url" t={t} />
            <CanalProLink testId="canal-pro-open-feed" t={t} />
          </Step>
          <Step number={2} title={t('canalPro.passoLeads')} t={t}>
            <CanalProLink testId="canal-pro-open-leads" t={t} />
          </Step>
          <Text className="m-0 text-xs text-(--text-tertiary) leading-relaxed">{t('canalPro.requisitos')}</Text>
        </div>
      </Modal>
    </div>
  );
}

/**
 * The Canal Pro mark (Grupo OLX colours) in a circle.
 *
 * @param props - Size in pixels.
 * @returns The badge.
 */
export function CanalProBadge({ size = 40 }: { size?: number }) {
  return (
    <Text
      component="span"
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-full font-extrabold shrink-0 text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.28), background: 'linear-gradient(135deg, #6E0AD6, #F28000)' }}
    >
      PRO
    </Text>
  );
}

/** Link that opens Canal Pro in a new tab. */
function CanalProLink({ testId, t }: { testId: string; t: Translate }) {
  return (
    <Anchor
      href={CANAL_PRO_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="self-start text-xs font-semibold text-[var(--seed-blue)] hover:underline"
    >
      {t('canalPro.abrirCanalPro')}
    </Anchor>
  );
}

/** One numbered Canal Pro setup step, with its "where to paste" hint and its content. */
function Step({ number, title, children, t }: { number: number; title: string; children?: React.ReactNode; t: Translate }) {
  return (
    <div className="flex flex-col gap-(--space-2) p-(--space-3) rounded-(--radius-md) border border-(--border-subtle)" data-testid={`canal-pro-step-${number}`}>
      <Title order={4} size="sm" className="m-0 font-semibold text-(--text-primary)">{`${number}. ${title}`}</Title>
      <List className="m-0 pl-(--space-4) text-xs text-(--text-secondary)">
        <ListItem>{t(number === 1 ? 'canalPro.passoFeedOnde' : 'canalPro.passoLeadsOnde')}</ListItem>
      </List>
      {children}
    </div>
  );
}

export function UrlField({ value, testId, t }: { value: string; testId: string; t: Translate }) {
  return (
    <div className="flex items-center gap-(--space-2)">
      <Text component="span" className="flex-1 min-w-0 truncate text-xs font-mono px-(--space-2) py-(--space-1-5) rounded-(--radius-sm) bg-[var(--surface-secondary)]" data-testid={testId}>
        {value}
      </Text>
      <CopyButton text={value} label={t('portal.copyButton.copiar')} copiedLabel={t('portal.copyButton.copiado')} />
    </div>
  );
}
